<template>
  <form
    class="message-composer"
    :class="{ 'drop-active': dropActive }"
    @submit.prevent="submit"
    @dragenter.prevent="handleDragEnter"
    @dragover.prevent="handleDragOver"
    @dragleave.prevent="handleDragLeave"
    @drop.prevent="handleDrop"
    @paste="handlePaste"
  >
    <div v-if="quoteMessage" class="quote-preview">
      <span>
        <strong>{{ language.t('composer.quote') }}</strong>
        {{ quoteMessage.quoteText || quoteMessage.text }}
      </span>
      <button type="button" :title="language.t('composer.cancelQuote')" @click="$emit('cancel-quote')">×</button>
    </div>
    <div class="composer-tools">
      <button type="button" :title="language.t('composer.emoji')" :aria-label="language.t('composer.emoji')" :disabled="disabled" @click="toggleEmojiPicker">☺</button>
      <button
        type="button"
        class="composer-tool-button image-tool"
        :title="language.t('composer.image')"
        :aria-label="language.t('composer.image')"
        :disabled="disabled"
        @click="chooseImage"
      >
        <svg class="composer-tool-icon" viewBox="0 0 24 24" aria-hidden="true">
          <rect x="3" y="5" width="18" height="14" rx="2.5"></rect>
          <circle cx="8.2" cy="9.2" r="1.6"></circle>
          <path d="M6.5 17l4.2-4.7 3.1 3.4 1.8-2 2.9 3.3"></path>
        </svg>
      </button>
      <button
        type="button"
        class="composer-tool-button file-tool"
        :title="language.t('composer.file')"
        :aria-label="language.t('composer.file')"
        :disabled="disabled"
        @click="chooseFile"
      >
        <svg class="composer-tool-icon" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M7 3.8h7.2L19 8.6V20a1.8 1.8 0 0 1-1.8 1.8H7A1.8 1.8 0 0 1 5.2 20V5.6A1.8 1.8 0 0 1 7 3.8z"></path>
          <path d="M14 4v5h5"></path>
          <path d="M8.5 13h7"></path>
          <path d="M8.5 16.5h5.2"></path>
        </svg>
      </button>
      <input ref="imageInput" type="file" accept="image/*" multiple @change="selectAttachment($event, 'image')">
      <input ref="fileInput" type="file" multiple @change="selectAttachment($event, 'file')">
      <div v-if="emojiOpen" class="emoji-picker" @click.stop>
        <button
          v-for="emoji in emojiList"
          :key="emoji"
          type="button"
          @click="insertEmoji(emoji)"
        >
          {{ emoji }}
        </button>
      </div>
    </div>
    <textarea
      ref="textareaEl"
      v-model="text"
      :disabled="disabled"
      :placeholder="language.t('composer.placeholder')"
      rows="3"
      @keydown.enter.exact.prevent="submit"
    ></textarea>
    <div class="composer-footer">
      <span>{{ language.t('composer.shortcut') }}</span>
      <button class="send-button" :disabled="disabled || !text.trim()">{{ language.t('composer.send') }}</button>
    </div>
    <Teleport to="body">
      <div v-if="pendingImages.length" class="image-send-preview-backdrop" :class="{ 'drop-active': previewDropActive }">
        <section class="image-send-preview" aria-label="图片发送预览">
          <header><strong>发送图片</strong><button type="button" class="image-preview-close" title="取消发送" @click="cancelImagePreview">×</button></header>
          <div class="image-preview-stage"><img :src="activePendingImage.url" :alt="activePendingImage.file.name || '待发送图片'"></div>
          <div class="image-preview-caption-row"><textarea v-model="previewCaption" rows="1" placeholder="添加图片说明"></textarea></div>
          <footer>
            <div class="image-preview-thumbnails">
              <button v-for="(item, index) in pendingImages" :key="item.url" type="button" class="image-preview-thumbnail" :class="{ active: index === activeImageIndex }" @click="activeImageIndex = index">
                <img :src="item.url" :alt="item.file.name || '待发送图片'">
                <span class="image-preview-remove" title="移除" @click.stop="removePendingImage(index)">×</span>
              </button>
              <button type="button" class="image-preview-add" title="继续添加图片" @click="chooseImage">+</button>
            </div>
            <button type="button" class="image-preview-send" :disabled="disabled" title="发送图片" @click="sendPendingImages">➤</button>
          </footer>
        </section>
      </div>
    </Teleport>
  </form>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useLanguageStore } from '@/stores/language';

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false
  },
  quoteMessage: {
    type: Object,
    default: null
  }
});

const emit = defineEmits(['send', 'send-attachment', 'cancel-quote']);
const language = useLanguageStore();
const text = ref('');
const emojiOpen = ref(false);
const dropActive = ref(false);
const textareaEl = ref(null);
const imageInput = ref(null);
const fileInput = ref(null);
const pendingImages = ref([]);
const activeImageIndex = ref(0);
const previewCaption = ref('');
const previewDropActive = ref(false);
const activePendingImage = computed(() => pendingImages.value[activeImageIndex.value] || pendingImages.value[0]);
let dragDepth = 0;
const emojiList = [
  '😀', '😄', '😂', '😊', '😍', '😘', '😎', '😢',
  '😡', '👍', '🙏', '👏', '💪', '🎉', '❤️', '🔥',
  '🌹', '☕', '✅', '⭐', '💰', '🎁', '🤝', '👌'
];

function submit() {
  const value = text.value.trim();
  if (!value) {
    return;
  }
  emit('send', value);
  text.value = '';
  emojiOpen.value = false;
}

function toggleEmojiPicker() {
  emojiOpen.value = !emojiOpen.value;
}

function insertEmoji(emoji) {
  const element = textareaEl.value;
  if (!element) {
    text.value += emoji;
    return;
  }
  const start = element.selectionStart ?? text.value.length;
  const end = element.selectionEnd ?? text.value.length;
  text.value = `${text.value.slice(0, start)}${emoji}${text.value.slice(end)}`;
  requestAnimationFrame(() => {
    element.focus();
    const cursor = start + emoji.length;
    element.setSelectionRange(cursor, cursor);
  });
}

function chooseImage() {
  imageInput.value?.click();
}

function chooseFile() {
  fileInput.value?.click();
}

function attachmentTypeForFile(file, fallbackType = 'file') {
  if (file?.type?.startsWith('image/')) {
    return 'image';
  }
  return fallbackType === 'image' ? 'image' : 'file';
}

function emitFiles(files, fallbackType = 'file') {
  if (props.disabled) {
    return;
  }
  const validFiles = Array.from(files || []).filter(file => file instanceof File && file.size > 0);
  if (!validFiles.length) {
    return;
  }

  const caption = text.value.trim();
  validFiles.forEach((file, index) => {
    emit('send-attachment', {
      type: attachmentTypeForFile(file, fallbackType),
      file,
      text: index === 0 ? caption : ''
    });
  });
  text.value = '';
  emojiOpen.value = false;
}

function stageImages(files) {
  if (props.disabled) return;
  const images = Array.from(files || []).filter(file => file instanceof File && file.size > 0 && file.type.startsWith('image/'));
  if (!images.length) return;
  if (!pendingImages.value.length) {
    previewCaption.value = text.value.trim();
    text.value = '';
  }
  pendingImages.value.push(...images.map(file => ({ file, url: URL.createObjectURL(file) })));
  activeImageIndex.value = pendingImages.value.length - images.length;
  emojiOpen.value = false;
}

function removePendingImage(index) {
  const [removed] = pendingImages.value.splice(index, 1);
  if (removed?.url) URL.revokeObjectURL(removed.url);
  activeImageIndex.value = Math.max(0, Math.min(activeImageIndex.value, pendingImages.value.length - 1));
  if (!pendingImages.value.length) previewCaption.value = '';
}

function cancelImagePreview() {
  pendingImages.value.forEach(item => URL.revokeObjectURL(item.url));
  pendingImages.value = [];
  activeImageIndex.value = 0;
  previewCaption.value = '';
}

function sendPendingImages() {
  if (props.disabled || !pendingImages.value.length) return;
  const images = pendingImages.value.map(item => item.file);
  const caption = previewCaption.value.trim();
  pendingImages.value.forEach(item => URL.revokeObjectURL(item.url));
  pendingImages.value = [];
  activeImageIndex.value = 0;
  previewCaption.value = '';
  images.forEach((file, index) => emit('send-attachment', { type: 'image', file, text: index === images.length - 1 ? caption : '' }));
}

function selectAttachment(event, type) {
  const input = event.target;
  const files = input.files ? Array.from(input.files) : [];
  input.value = '';
  if (!files.length || props.disabled) {
    return;
  }
  if (type === 'image') {
    stageImages(files);
    return;
  }
  emitFiles(files, type);
}

function hasDraggedFiles(event) {
  const types = Array.from(event.dataTransfer?.types || []);
  return types.includes('Files');
}

function handleDragEnter(event) {
  if (props.disabled || !hasDraggedFiles(event)) {
    return;
  }
  dragDepth += 1;
  dropActive.value = true;
  event.dataTransfer.dropEffect = 'copy';
}

function handleDragOver(event) {
  if (props.disabled || !hasDraggedFiles(event)) {
    return;
  }
  dropActive.value = true;
  event.dataTransfer.dropEffect = 'copy';
}

function handleDragLeave() {
  dragDepth = Math.max(0, dragDepth - 1);
  if (dragDepth === 0) {
    dropActive.value = false;
  }
}

function filesFromDataTransfer(dataTransfer) {
  const items = Array.from(dataTransfer?.items || []);
  const files = items
    .filter(item => item.kind === 'file' && !item.webkitGetAsEntry?.()?.isDirectory)
    .map(item => item.getAsFile())
    .filter(Boolean);
  if (files.length) {
    return files;
  }
  return Array.from(dataTransfer?.files || []);
}

function handleDrop(event) {
  dragDepth = 0;
  dropActive.value = false;
  if (props.disabled) {
    return;
  }
  const files = filesFromDataTransfer(event.dataTransfer);
  stageImages(files.filter(file => file.type.startsWith('image/')));
  emitFiles(files.filter(file => !file.type.startsWith('image/')));
}

function imageFilesFromClipboard(clipboardData) {
  return Array.from(clipboardData?.items || [])
    .filter(item => item.kind === 'file' && item.type.startsWith('image/'))
    .map(item => item.getAsFile())
    .filter(Boolean);
}

function handlePaste(event) {
  if (props.disabled) {
    return;
  }
  const imageFiles = imageFilesFromClipboard(event.clipboardData);
  if (!imageFiles.length) {
    return;
  }
  event.preventDefault();
  stageImages(imageFiles);
}

function handlePreviewPaste(event) {
  if (!pendingImages.value.length || event.defaultPrevented) return;
  const imageFiles = imageFilesFromClipboard(event.clipboardData);
  if (!imageFiles.length) return;
  event.preventDefault();
  stageImages(imageFiles);
}

function handlePreviewDragOver(event) {
  if (!pendingImages.value.length || !hasDraggedFiles(event)) return;
  event.preventDefault();
  previewDropActive.value = true;
  event.dataTransfer.dropEffect = 'copy';
}

function handlePreviewDragLeave(event) {
  if (!event.relatedTarget) previewDropActive.value = false;
}

function handlePreviewDrop(event) {
  if (!pendingImages.value.length || event.defaultPrevented) return;
  const images = filesFromDataTransfer(event.dataTransfer).filter(file => file.type.startsWith('image/'));
  previewDropActive.value = false;
  if (!images.length) return;
  event.preventDefault();
  stageImages(images);
}

onMounted(() => {
  window.addEventListener('paste', handlePreviewPaste);
  window.addEventListener('dragover', handlePreviewDragOver);
  window.addEventListener('dragleave', handlePreviewDragLeave);
  window.addEventListener('drop', handlePreviewDrop);
});

onBeforeUnmount(() => {
  cancelImagePreview();
  window.removeEventListener('paste', handlePreviewPaste);
  window.removeEventListener('dragover', handlePreviewDragOver);
  window.removeEventListener('dragleave', handlePreviewDragLeave);
  window.removeEventListener('drop', handlePreviewDrop);
});
</script>
